// 02 — the recorder: capability, the capture machine's state as a row of
// lamps, the clock against the two-minute cap, the controls, and playback.
// Every control calls a handler the screen owns; nothing here starts,
// stops or submits on its own.
import { Button } from "@bindery/ui/components/ui/button";
import { cn } from "@bindery/ui/lib/utils";
import type { CaptureState, SpeechCapture } from "@bindery/speech/capture";
import { Mic, RotateCcw, Send, Square } from "lucide-react";
import { useNow } from "../lib/hooks";
import { bytes, clock } from "../lib/format";
import { ErrorReadout } from "./error-readout";
import { Absent, Panel, Readout, Readouts } from "./panel";

type Tag = CaptureState["tag"];

// The machine's states in the order a capture passes through them, then
// the three places it can stop instead.
const LAMPS: readonly { tag: Tag; label: string }[] = [
  { tag: "idle", label: "idle" },
  { tag: "requesting-permission", label: "permission" },
  { tag: "recording", label: "rec" },
  { tag: "stopping", label: "stopping" },
  { tag: "recorded", label: "recorded" },
  { tag: "blocked", label: "blocked" },
  { tag: "rejected", label: "rejected" },
  { tag: "unsupported", label: "unsupported" },
];

const LIT: Readonly<Record<Tag, string>> = {
  idle: "bg-foreground/60",
  "requesting-permission": "bg-[var(--lab-signal)]",
  recording: "bg-[var(--lab-rec)] lab-pulse",
  stopping: "bg-[var(--lab-signal)]",
  recorded: "bg-[var(--lab-ok)]",
  blocked: "bg-[var(--lab-rec)]",
  rejected: "bg-[var(--lab-rec)]",
  unsupported: "bg-[var(--lab-rec)]",
};

function Lamps({ current }: { current: Tag }) {
  return (
    <ol className="flex flex-wrap gap-x-3 gap-y-1.5" aria-label="Capture state">
      {LAMPS.map(({ tag, label }) => {
        const on = tag === current;
        return (
          <li
            key={tag}
            aria-current={on ? "step" : undefined}
            className={cn(
              "lab-label flex items-center gap-1.5",
              on && "text-foreground",
            )}
          >
            <span
              className={cn(
                "size-2 rounded-full border border-current/30",
                on ? LIT[tag] : "bg-transparent",
              )}
            />
            {label}
          </li>
        );
      })}
    </ol>
  );
}

// The clock and the bar under it. While recording it counts up against
// the cap, with the warn point marked; the bar turns amber at the warn cue.
function Meter({ capture }: { capture: SpeechCapture }) {
  const { state, limits } = capture;
  const recording = state.tag === "recording";
  const now = useNow(recording);
  const elapsed =
    state.tag === "recording"
      ? Math.max(0, now - state.startedAt)
      : state.tag === "recorded"
        ? state.recording.meta.durationMs
        : 0;
  const warned = state.tag === "recording" && state.warned;
  const fraction = Math.min(1, elapsed / limits.maxDurationMs);
  const warnAt = limits.warnAtMs / limits.maxDurationMs;
  const caption =
    state.tag === "recording"
      ? warned
        ? `almost done — ${clock(limits.maxDurationMs - elapsed)} left`
        : `${clock(limits.maxDurationMs - elapsed)} to the cap`
      : state.tag === "stopping"
        ? state.endedBy === "max-duration"
          ? "time's up — finishing"
          : "finishing"
        : state.tag === "recorded"
          ? `ended by ${state.recording.meta.endedBy}`
          : `cap ${clock(limits.maxDurationMs)} · warn at ${clock(limits.warnAtMs)} · min ${clock(limits.minDurationMs)}`;
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-end justify-between gap-3">
        <span
          className={cn(
            "lab-readout text-5xl leading-none font-medium tracking-tight",
            state.tag === "recording" && !warned && "text-[var(--lab-rec)]",
            warned && "text-[oklch(0.62_0.16_65)]",
          )}
        >
          {clock(elapsed)}
        </span>
        <span
          className={cn(
            "lab-readout pb-1 text-right text-xs",
            warned
              ? "font-medium text-[oklch(0.55_0.15_60)]"
              : "text-muted-foreground",
          )}
          aria-live="polite"
        >
          {caption}
        </span>
      </div>
      <div className="bg-muted relative h-2 overflow-hidden rounded-[1px] border">
        <div
          className={cn(
            "absolute inset-y-0 left-0 transition-[width] duration-100 ease-linear",
            warned ? "bg-[var(--lab-signal)]" : "bg-foreground/70",
            state.tag === "recording" && !warned && "bg-[var(--lab-rec)]",
          )}
          style={{ width: `${String(fraction * 100)}%` }}
        />
        <div
          className="bg-foreground/50 absolute inset-y-0 w-px"
          style={{ left: `${String(warnAt * 100)}%` }}
          title="warn cue"
        />
      </div>
    </div>
  );
}

export function CapturePanel({
  capture,
  playbackUrl,
  canSubmit,
  submitting,
  onStart,
  onStop,
  onRedo,
  onSubmit,
}: {
  capture: SpeechCapture;
  playbackUrl: string | null;
  canSubmit: boolean;
  submitting: boolean;
  onStart: () => void;
  onStop: () => void;
  onRedo: () => void;
  onSubmit: () => void;
}) {
  const { state, capability } = capture;
  const tag = state.tag;
  const canStart = tag === "idle" || tag === "blocked" || tag === "rejected";
  return (
    <Panel
      index="02"
      title="Capture"
      aside={
        <span className="lab-readout text-muted-foreground text-xs">
          {capability.tag === "supported"
            ? capability.mime
            : `unsupported: ${capability.reason}`}
        </span>
      }
    >
      <Lamps current={tag} />
      <Meter capture={capture} />

      <div className="flex flex-wrap gap-2">
        {tag === "recording" ? (
          <Button variant="destructive" onClick={onStop}>
            <Square className="fill-current" /> Stop
          </Button>
        ) : (
          <Button
            variant="outline"
            onClick={onStart}
            disabled={!canStart}
            className="border-[var(--lab-rec)]/50"
          >
            <Mic className="text-[var(--lab-rec)]" />
            {tag === "blocked" ? "Ask again" : "Record"}
          </Button>
        )}
        <Button
          variant="ghost"
          onClick={onRedo}
          disabled={tag !== "recorded" && tag !== "rejected"}
        >
          <RotateCcw /> Redo
        </Button>
        <Button
          onClick={onSubmit}
          disabled={!canSubmit}
          className="ml-auto min-w-36"
        >
          <Send />
          {submitting ? "Transcribing…" : "Transcribe"}
        </Button>
      </div>

      {state.tag === "blocked" || state.tag === "rejected" ? (
        <ErrorReadout error={state.error} />
      ) : state.tag === "unsupported" ? (
        <ErrorReadout error={{ kind: "unsupported", reason: state.reason }} />
      ) : null}

      {state.tag === "recorded" ? (
        <div className="flex flex-col gap-2 border-t pt-3">
          {playbackUrl === null ? null : (
            // oxlint-disable-next-line jsx-a11y/media-has-caption -- plays back the user's own just-made recording; there is no transcript to caption it with
            <audio
              controls
              src={playbackUrl}
              className="h-9 w-full"
              aria-label="Playback of the recording"
            />
          )}
          <Readouts>
            <Readout label="mimeType">{state.recording.meta.mime}</Readout>
            <Readout label="duration">
              {clock(state.recording.meta.durationMs)} (
              {state.recording.meta.durationMs} ms)
            </Readout>
            <Readout label="size">
              {bytes(state.recording.meta.bytes)} (
              {state.recording.meta.bytes.toLocaleString("en-US")} B)
            </Readout>
            <Readout label="ended by">{state.recording.meta.endedBy}</Readout>
          </Readouts>
        </div>
      ) : tag === "idle" ? (
        <p className="text-muted-foreground text-xs">
          <Absent>No recording.</Absent> Record asks for the microphone on the
          first tap only.
        </p>
      ) : null}
    </Panel>
  );
}
